import React, { useEffect, useState } from "react";
import FlowBuilderDrawer from "./Drawer/FlowBuilderDrawer";
import FlowBuilderHeader from "./Header/FlowBuilderHeader";
import { useThrottle } from "react-use";
import { useAppDispatch, useAppSelector } from "store/hooks";
import FlowBuilderSegmentEditor from "./FlowBuilderSegmentEditor";
import FlowBuilderReview from "./FlowBuilderReview";
import { useParams } from "react-router-dom";
import ApiService from "services/api.service";
import { MessageNodeData, NodeData } from "./Nodes/NodeData";
import { Edge, Node } from "reactflow";
import { EdgeData } from "./Edges/EdgeData";
import {
  clearSegmentPanelErrors,
  defaultJourneyEntrySettings,
  defaultJourneySettings,
  JourneyEntrySettings,
  JourneySettings,
  JourneyType,
  loadVisualLayout,
  SegmentsSettings,
  setAvailableTags,
  setFlowId,
  setFlowName,
  setFlowStatus,
  setJourneyEntrySettings,
  setJourneySettings,
  setJourneyType,
  setNodes,
  setSegmentsSettings,
  setShowSegmentsErrors,
  setTemplateInlineCreator,
} from "reducers/flow-builder.reducer";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { JourneyStatus } from "pages/JourneyTablev2/JourneyTablev2";
import { SocketProvider } from "./useDevSocketConnection";
import { capitalize } from "lodash";
import PushBuilder from "pages/PushBuilder/PushBuilder";
import { MessageType } from "types/Workflow";
import FlowFormEditor, { NodeType } from "./FlowFormEditor";
import { useJourneyControls } from "./hooks";

const FlowBuilderV2 = () => {
  const { id } = useParams();
  const { setSimpleJourney, simpleJourney, updateJourney, onSaveJourneyAsDraft, onStartJourney, isActive } = useJourneyControls();

  const [isLoading, setIsLoading] = useState(true);

  const dispatch = useAppDispatch();
  const flowBuilderState = useAppSelector((state) => state.flowBuilder);

  // const throttledFlowBuilderState = useThrottle(flowBuilderState, 1000);
  const loadJourney = async () => {
    setIsLoading(true);
    try {
      const { data } = await ApiService.get<{
        name: string;
        nodes: Node<NodeData>[];
        edges: Edge<EdgeData>[];
        segments: SegmentsSettings;
        isDynamic: boolean;
        isActive: boolean;
        isPaused: boolean;
        isStopped: boolean;
        isDeleted: boolean;
        journeyEntrySettings?: JourneyEntrySettings;
        journeySettings?: JourneySettings;
      }>({
        url: "/journeys/" + id,
      });

      dispatch(setFlowName(data.name));
      if (
        data.nodes.length !== 0 &&
        data.nodes.some((node) => node.type === NodeType.START)
      ) {
        dispatch(loadVisualLayout({ nodes: data.nodes, edges: data.edges }));
      }

      dispatch(setSegmentsSettings(data.segments));
      dispatch(
        setJourneyType(
          data.isDynamic ? JourneyType.DYNAMIC : JourneyType.STATIC
        )
      );
      dispatch(setShowSegmentsErrors(false));
      dispatch(clearSegmentPanelErrors());
      dispatch(
        setJourneyEntrySettings(
          data?.journeyEntrySettings || defaultJourneyEntrySettings
        )
      );
      dispatch(
        setJourneySettings(data?.journeySettings || defaultJourneySettings)
      );
      console.log("isActive", data.isActive);
      dispatch(setFlowStatus(data.isActive ? JourneyStatus.ACTIVE : JourneyStatus.DRAFT));
      dispatch(setFlowId(id));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadJourney();

    return () => {
      dispatch(setTemplateInlineCreator(undefined));
    };
  }, []);

  const TemplateRender = () => {
    if (!flowBuilderState.templateInlineCreation) return <></>;

    const templates: Record<string, React.ReactNode> = {
      [MessageType.PUSH]: <PushBuilder isInlineCreator />,
    };

    return templates?.[flowBuilderState.templateInlineCreation.type] || <></>;
  };

  return (
    <SocketProvider>
      <>
        <div
          className={`${flowBuilderState.templateInlineCreation && "hidden"
            } relative w-full h-full`}
        >
          <FlowBuilderHeader
            onSave={onSaveJourneyAsDraft}
            onStart={onStartJourney}
            isActive={isActive}
          />
          <div className="relative flex w-full h-full max-h-[calc(100%-60px)]">
            <FlowFormEditor
              key={String(isActive)}
              setSimpleJourney={setSimpleJourney}
              simpleJourney={simpleJourney}
              updateJourney={updateJourney}
              isActive={isActive}
            />
          </div>
        </div>
        {TemplateRender()}
      </>
    </SocketProvider>
  );
};

export default FlowBuilderV2;
